import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { ArrowRight, Star, Camera, PlayCircle, Clock, CameraOff, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import StatsSection from "@/components/realizacja/StatsSection";
import ComparisonSection from "@/components/realizacja/ComparisonSection";

const REALIZACJA_VIDEO_URL = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/viraluj_realizacja.mp4";
const REALIZACJA_POSTER_URL = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/viralisek_realizacja_tworz_viralowe_video.png.jpg";

const STEPS = [
  {
    icon: Star,
    title: "Wybierz scenariusze",
    description: "Oznacz ulubione skrypty w panelu. My przygotujemy plan nagrań.",
  },
  {
    icon: Camera,
    title: "My przyjeżdżamy",
    description: "Kamery, światła, prompter — wszystko mamy. Ty musisz być tylko 'trochę' przygotowany.",
  },
  {
    icon: PlayCircle,
    title: "Odbierasz gotowce",
    description: "Montaż, napisy, kolorystyka — 1 dzień nagrań = content na miesiąc.",
  },
] as const;

const FEATURES = [
  { emoji: "🎬", text: "Profesjonalny sprzęt na miejscu" },
  { emoji: "📺", text: "Prompter — nie musisz uczyć się tekstu" },
  { emoji: "💡", text: "Oświetlenie studyjne" },
  { emoji: "✂️", text: "Montaż i postprodukcja" },
  { emoji: "📱", text: "Gotowe pliki do publikacji" },
  { emoji: "🚀", text: "Content na cały miesiąc w jeden dzień" },
] as const;

const TARGET_AUDIENCE = [
  {
    icon: Clock,
    title: "Brakuje Ci czasu",
    description: "Jeden dzień nagrań zastępuje tygodnie samodzielnego tworzenia contentu.",
  },
  {
    icon: CameraOff,
    title: "Nie masz sprzętu",
    description: "Przywozimy wszystko — kamery, światła, mikrofony. Ty stawiasz się na miejscu.",
  },
  {
    icon: Sparkles,
    title: "Chcesz pro efekt bez stresu",
    description: "Profesjonalna jakość bez konieczności nauki montażu i postprodukcji.",
  },
] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const Realizacja = () => {
  const navigate = useNavigate();
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleCanPlay = useCallback(() => {
    setVideoReady(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const handleCTA = () => {
    navigate("/kontakt");
  };

  return (
    <div className="min-h-screen text-gray-900" style={{ background: "linear-gradient(to bottom, #F5A07A 0%, #FCCAB0 8%, #FDD3BC 20%, #FDDCCB 35%, #FDE8DC 50%, #ffffff 60%, #ffffff 70%, #FDE8DC 80%, #FCCAB0 100%)" }}>
      <Header />

      {/* Hero with video background */}
      <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${REALIZACJA_POSTER_URL})` }}
        />

        <video
          ref={videoRef}
          muted
          loop
          playsInline
          poster={REALIZACJA_POSTER_URL}
          preload="auto"
          onCanPlay={handleCanPlay}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: videoReady ? 1 : 0 }}
        >
          <source src={REALIZACJA_VIDEO_URL} type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at center, transparent 50%, rgba(0, 0, 0, 0.3) 100%)",
          }}
        />

        <div className="relative z-10 flex items-end md:items-center justify-center w-full px-4 sm:px-6 lg:px-8 pb-24 md:pb-0 pt-20 md:pt-32">
          <div
            className="text-center max-w-3xl w-full rounded-3xl p-8 md:p-12 overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.04)",
              backdropFilter: "blur(10px) saturate(120%)",
              WebkitBackdropFilter: "blur(10px) saturate(120%)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            }}
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white"
            >
              <span className="block">Masz scenariusz<span className="text-primary">.</span></span>
              <span className="block">My mamy kamerę<span className="text-primary">.</span></span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mt-6 text-base md:text-lg text-white/90 max-w-md mx-auto font-medium"
            >
              Przyjeżdżamy. Nagrywamy. Montujemy.
              <br />
              <span className="text-primary">Ty tylko mówisz.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-8 flex items-center justify-center"
            >
              <Button
                size="lg"
                onClick={handleCTA}
                className="group relative px-8 py-6 text-base font-semibold bg-primary text-white hover:bg-primary/90 rounded-full transition-all shadow-lg shadow-primary/25 overflow-hidden active:scale-[0.98]"
              >
                <span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer-sweep 4s ease-in-out infinite",
                  }}
                />
                <span className="relative z-10 flex items-center">
                  Umów bezpłatną konsultację
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <StatsSection />

      {/* Jak to działa */}
      <section className="py-20 md:py-28 relative">
        <div className="relative z-10 max-w-5xl mx-auto px-4">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={0}
            className="text-3xl md:text-5xl font-bold text-center mb-4 tracking-tight text-gray-900"
          >
            Jak to działa<span className="text-primary">?</span>
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="text-base md:text-lg text-gray-600 text-center mb-14 max-w-xl mx-auto"
          >
            Trzy proste kroki od scenariusza do gotowego materiału.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 2}
                className="group rounded-2xl p-8 text-center transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: "rgba(255, 255, 255, 0.45)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                  <step.icon className="w-7 h-7 text-primary" />
                </div>
                <div className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Krok {i + 1}</div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ComparisonSection />

      {/* Co dostajesz */}
      <section className="py-20 md:py-28 relative">
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={0}
            className="text-3xl md:text-5xl font-bold text-center mb-14 tracking-tight text-gray-900"
          >
            Co dostajesz<span className="text-primary">?</span>
          </motion.h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.text}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={fadeUp}
                custom={i}
                className="flex items-center gap-4 p-4 rounded-xl transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: "rgba(255, 255, 255, 0.45)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                }}
              >
                <span className="text-2xl flex-shrink-0">{feature.emoji}</span>
                <span className="text-gray-900 font-medium text-sm">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dla kogo */}
      <section className="py-20 md:py-28 relative">
        <div className="relative z-10 max-w-5xl mx-auto px-4">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={0}
            className="text-3xl md:text-5xl font-bold text-center mb-4 tracking-tight text-gray-900"
          >
            Dla kogo<span className="text-primary">?</span>
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="text-base md:text-lg text-gray-600 text-center mb-14 max-w-xl mx-auto"
          >
            Jeśli któryś z tych punktów pasuje do Ciebie — ta usługa jest dla Ciebie.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {TARGET_AUDIENCE.map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 2}
                className="group rounded-2xl p-8 text-center transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: "rgba(255, 255, 255, 0.45)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 md:py-28 relative">
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center space-y-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={0}
            className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900"
          >
            Gotowy żeby nagrać
            <br />
            <span className="text-primary">content na miesiąc?</span>
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={1}
          >
            <Button
              size="lg"
              onClick={handleCTA}
              className="group relative px-8 py-6 text-base font-semibold bg-primary text-white hover:bg-primary/90 rounded-full transition-all shadow-lg shadow-primary/25 overflow-hidden active:scale-[0.98]"
            >
              <span
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer-sweep 4s ease-in-out infinite",
                }}
              />
              <span className="relative z-10 flex items-center">
                Umów bezpłatną konsultację
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
          </motion.div>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={2}
            className="text-sm text-gray-500"
          >
            Bezpłatna rozmowa 15 min — sprawdzimy czy to dla Ciebie
          </motion.p>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default Realizacja;
